import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { syncManager } from '../lib/syncManager';
import { useSocialBondsStore, SocialBond } from './socialBondsStore';
import { onSignOut } from '../lib/signOutReset';
import {
  AvocationalPathName,
  resolveAvocationalPath,
} from '../constants/avocationalPaths';

export type SkillRank = 'Beginner' | 'Novice' | 'Intermediate' | 'Advanced' | 'Expert' | 'Master';
export type EnergyType = 'energizing' | 'restorative' | 'flow_state';

/** A hobby's gear (AV4) — a tiny per-hobby kit list with a light status. */
export type GearStatus = 'Wishlist' | 'Owned' | 'Favorite';
export interface HobbyGear {
  id: string;
  name: string;
  status: GearStatus;
}

export interface AvocationalHobby {
  id: string;
  userId: string;
  name: string;
  description: string;
  icon: string;
  color: string;
  skillRank: SkillRank;
  currentXp: number;
  totalXp: number;
  totalTimeMinutes: number;
  sessionCount: number;
  averageSatisfaction: number;
  isActive: boolean;
  /** AV0 — the archetype Paths this hobby belongs to (replaces leisure modes). */
  primaryPath: AvocationalPathName;
  secondaryPaths: AvocationalPathName[];
  /** AV4 — per-hobby gear/kit (rides the `avocational_hobbies.gear` jsonb column). */
  gear: HobbyGear[];
  createdAt: string;
  updatedAt: string;
}

export interface AvocationalActivity {
  id: string;
  userId: string;
  hobbyId: string;
  name: string;
  durationMinutes: number;
  satisfactionRating: number;
  energyType: EnergyType;
  isSocial: boolean;
  isActive: boolean;
  isCreative: boolean;
  notes: string;
  xpEarned: number;
  modeXpDistribution: Record<string, number>;
  loggedAt: string;
  createdAt: string;
}

export interface AvocationalActivityBond {
  id: string;
  activityId: string;
  bondId: string;
  xpShared: number;
  createdAt: string;
}

export interface AvocationalParameter {
  id: string;
  userId: string;
  parameterKey: string;
  parameterValue: number;
  targetValue: number;
  unit: string;
  updatedAt: string;
}

interface CreateHobbyInput {
  name: string;
  description?: string;
  icon?: string;
  color?: string;
  primaryPath: AvocationalPathName;
  secondaryPaths?: AvocationalPathName[];
}

interface LogActivityInput {
  hobbyId: string;
  name: string;
  durationMinutes: number;
  satisfactionRating: number;
  energyType: EnergyType;
  isSocial: boolean;
  isActive: boolean;
  isCreative: boolean;
  notes?: string;
  bondIds?: string[];
  loggedAt?: string;
}

const SKILL_RANK_XP_THRESHOLDS: Record<SkillRank, number> = {
  Beginner: 0,
  Novice: 500,
  Intermediate: 2000,
  Advanced: 5000,
  Expert: 10000,
  Master: 20000,
};

const getSkillRankFromXP = (xp: number): SkillRank => {
  if (xp >= 20000) return 'Master';
  if (xp >= 10000) return 'Expert';
  if (xp >= 5000) return 'Advanced';
  if (xp >= 2000) return 'Intermediate';
  if (xp >= 500) return 'Novice';
  return 'Beginner';
};

const calculateActivityXP = (
  durationMinutes: number,
  satisfactionRating: number,
  isCreative: boolean,
  isActive: boolean
): number => {
  const baseXP = Math.floor(durationMinutes * 2);
  const satisfactionMultiplier = 0.8 + (satisfactionRating * 0.1);
  const creativeBonus = isCreative ? 1.2 : 1.0;
  const activeBonus = isActive ? 1.1 : 1.0;
  return Math.floor(baseXP * satisfactionMultiplier * creativeBonus * activeBonus);
};

/**
 * Map a DB `avocational_hobbies` row → `AvocationalHobby`, resolving the Paths.
 * Every row carries `primary_path`/`secondary_paths` since the AV0 migration
 * backfilled them; the resolver defaults anything missing to `Artist`.
 */
function mapHobbyRow(h: any): AvocationalHobby {
  const primaryPath = resolveAvocationalPath(h.primary_path);
  const secondarySource: string[] = h.secondary_paths || [];
  const secondaryPaths = Array.from(
    new Set(secondarySource.map(resolveAvocationalPath).filter(p => p !== primaryPath)),
  );
  return {
    id: h.id,
    userId: h.user_id,
    name: h.name,
    description: h.description || '',
    icon: h.icon,
    color: h.color,
    skillRank: h.skill_rank as SkillRank,
    currentXp: h.current_xp,
    totalXp: h.total_xp,
    totalTimeMinutes: h.total_time_minutes,
    sessionCount: h.session_count,
    averageSatisfaction: parseFloat(h.average_satisfaction) || 0,
    isActive: h.is_active,
    primaryPath,
    secondaryPaths,
    gear: (h.gear ?? []) as HobbyGear[],
    createdAt: h.created_at,
    updatedAt: h.updated_at,
  };
}

interface AvocationalStore {
  hobbies: AvocationalHobby[];
  activities: AvocationalActivity[];
  activityBonds: AvocationalActivityBond[];
  parameters: AvocationalParameter[];
  isLoading: boolean;
  error: string | null;

  fetchHobbies: () => Promise<void>;
  fetchActivities: (hobbyId?: string, limit?: number) => Promise<void>;
  createHobby: (input: CreateHobbyInput) => Promise<AvocationalHobby | null>;
  updateHobby: (id: string, updates: Partial<CreateHobbyInput & { isActive: boolean; gear: HobbyGear[] }>) => Promise<void>;
  deleteHobby: (id: string) => Promise<void>;
  logActivity: (input: LogActivityInput) => Promise<AvocationalActivity | null>;
  getHobby: (id: string) => AvocationalHobby | undefined;
  getHobbiesByRank: (rank: SkillRank) => AvocationalHobby[];
  getActiveHobbies: () => AvocationalHobby[];
  getRecentActivities: (limit?: number) => AvocationalActivity[];
  getActivitiesForHobby: (hobbyId: string) => AvocationalActivity[];
  getTotalAvocationalXP: () => number;
  getTotalTimeInvested: () => number;
  getAverageSatisfaction: () => number;
  getBondsForActivity: (activityId: string) => SocialBond[];
  getDomainStats: () => {
    totalHobbies: number;
    activeHobbies: number;
    totalSessions: number;
    totalTimeHours: number;
    totalXP: number;
    averageSatisfaction: number;
    byRank: Record<SkillRank, number>;
  };
  clearStore: () => void;
}

/**
 * ⚠️⚠️ MOVED BY `clearStore` (sign-out). A load or a write notes it before its first
 * await and drops its answer when it has moved: work still in flight for the account
 * that signed out must not land in the next account's empty store.
 */
let epoch = 0;

/** An action's failure — shown only while the account it ran for is still signed in. */
function failed(at: number, error: unknown) {
  if (at === epoch) useAvocationalStore.setState({ error: (error as Error).message });
}

export const useAvocationalStore = create<AvocationalStore>((set, get) => ({
  hobbies: [],
  activities: [],
  activityBonds: [],
  parameters: [],
  isLoading: false,
  error: null,

  fetchHobbies: async () => {
    const at = epoch;
    set({ isLoading: true, error: null });
    try {
      const { user } = await syncManager.safeAuthCheck();
      if (!user) {
        set({ isLoading: false });
        return;
      }

      const { data, error } = await supabase
        .from('avocational_hobbies')
        .select('*')
        .eq('user_id', user.id)
        .order('updated_at', { ascending: false });
      if (at !== epoch) return; // signed out while it read

      if (error) throw error;

      const hobbies: AvocationalHobby[] = (data || []).map(mapHobbyRow);

      set({ hobbies, isLoading: false });
    } catch (error) {
      if (at === epoch) set({ error: (error as Error).message, isLoading: false });
    }
  },

  fetchActivities: async (hobbyId, limit = 50) => {
    const at = epoch;
    try {
      const { user } = await syncManager.safeAuthCheck();
      if (!user) return;

      let query = supabase
        .from('avocational_activities')
        .select('*')
        .eq('user_id', user.id)
        .order('logged_at', { ascending: false })
        .limit(limit);

      if (hobbyId) {
        query = query.eq('hobby_id', hobbyId);
      }

      const { data, error } = await query;

      if (error) throw error;

      const activities: AvocationalActivity[] = (data || []).map(a => ({
        id: a.id,
        userId: a.user_id,
        hobbyId: a.hobby_id,
        name: a.name,
        durationMinutes: a.duration_minutes,
        satisfactionRating: a.satisfaction_rating,
        energyType: a.energy_type as EnergyType,
        isSocial: a.is_social,
        isActive: a.is_active,
        isCreative: a.is_creative,
        notes: a.notes || '',
        xpEarned: a.xp_earned,
        modeXpDistribution: a.mode_xp_distribution || {},
        loggedAt: a.logged_at,
        createdAt: a.created_at,
      }));

      const activityIds = activities.map(a => a.id);
      if (activityIds.length > 0) {
        const { data: bondsData } = await supabase
          .from('avocational_activity_bonds')
          .select('*')
          .in('activity_id', activityIds);
        // Signed out while either read ran: neither the sessions nor their bonds land.
        // (No sessions → nothing to land: the reset's empty list stands.)
        if (at !== epoch) return;

        if (bondsData) {
          const activityBonds: AvocationalActivityBond[] = bondsData.map(b => ({
            id: b.id,
            activityId: b.activity_id,
            bondId: b.bond_id,
            xpShared: b.xp_shared,
            createdAt: b.created_at,
          }));
          set({ activityBonds });
        }
      }

      set({ activities });
    } catch (error) {
      failed(at, error);
    }
  },

  createHobby: async (input) => {
    const at = epoch;
    try {
      const { user } = await syncManager.safeAuthCheck();
      if (!user) return null;

      const secondaryPaths = (input.secondaryPaths || []).filter(p => p !== input.primaryPath);

      const { data, error } = await supabase
        .from('avocational_hobbies')
        .insert({
          user_id: user.id,
          name: input.name,
          description: input.description || '',
          icon: input.icon || 'Palette',
          color: input.color || '#10B981',
          skill_rank: 'Beginner',
          current_xp: 0,
          total_xp: 0,
          total_time_minutes: 0,
          session_count: 0,
          average_satisfaction: 0,
          is_active: true,
          primary_path: input.primaryPath,
          secondary_paths: secondaryPaths,
        })
        .select()
        .single();

      if (error) throw error;

      await get().fetchHobbies();

      return mapHobbyRow(data);
    } catch (error) {
      failed(at, error);
      return null;
    }
  },

  updateHobby: async (id, updates) => {
    const at = epoch;
    try {
      const dbUpdates: Record<string, unknown> = {
        updated_at: new Date().toISOString(),
      };

      if (updates.name !== undefined) dbUpdates.name = updates.name;
      if (updates.description !== undefined) dbUpdates.description = updates.description;
      if (updates.icon !== undefined) dbUpdates.icon = updates.icon;
      if (updates.color !== undefined) dbUpdates.color = updates.color;
      if (updates.isActive !== undefined) dbUpdates.is_active = updates.isActive;
      if (updates.gear !== undefined) dbUpdates.gear = updates.gear;
      if (updates.primaryPath !== undefined || updates.secondaryPaths !== undefined) {
        const current = get().hobbies.find(h => h.id === id);
        const primaryPath = updates.primaryPath ?? current?.primaryPath ?? 'Artist';
        const secondaryPaths = (updates.secondaryPaths ?? current?.secondaryPaths ?? []).filter(
          p => p !== primaryPath,
        );
        dbUpdates.primary_path = primaryPath;
        dbUpdates.secondary_paths = secondaryPaths;
      }

      const { error } = await supabase
        .from('avocational_hobbies')
        .update(dbUpdates)
        .eq('id', id);

      if (error) throw error;

      await get().fetchHobbies();
    } catch (error) {
      failed(at, error);
    }
  },

  deleteHobby: async (id) => {
    const at = epoch;
    try {
      const { error } = await supabase
        .from('avocational_hobbies')
        .delete()
        .eq('id', id);

      if (error) throw error;

      await get().fetchHobbies();
    } catch (error) {
      failed(at, error);
    }
  },

  logActivity: async (input) => {
    const at = epoch;
    try {
      const { user } = await syncManager.safeAuthCheck();
      if (!user) return null;

      const hobby = get().hobbies.find(h => h.id === input.hobbyId);
      if (!hobby) throw new Error('Hobby not found');

      const xpEarned = calculateActivityXP(
        input.durationMinutes,
        input.satisfactionRating,
        input.isCreative,
        input.isActive
      );

      // AV8 — leisure modes retired: no more activity→mode XP distribution.
      const modeXpDistribution: Record<string, number> = {};

      const { data: activityData, error: activityError } = await supabase
        .from('avocational_activities')
        .insert({
          user_id: user.id,
          hobby_id: input.hobbyId,
          name: input.name,
          duration_minutes: input.durationMinutes,
          satisfaction_rating: input.satisfactionRating,
          energy_type: input.energyType,
          is_social: input.isSocial,
          is_active: input.isActive,
          is_creative: input.isCreative,
          notes: input.notes || '',
          xp_earned: xpEarned,
          mode_xp_distribution: modeXpDistribution,
          logged_at: input.loggedAt || new Date().toISOString(),
        })
        .select()
        .single();

      if (activityError) throw activityError;

      if (input.isSocial && input.bondIds && input.bondIds.length > 0) {
        const xpPerBond = Math.floor(xpEarned * 0.25 / input.bondIds.length);

        const bondInserts = input.bondIds.map(bondId => ({
          activity_id: activityData.id,
          bond_id: bondId,
          xp_shared: xpPerBond,
        }));

        await supabase
          .from('avocational_activity_bonds')
          .insert(bondInserts);

        for (const bondId of input.bondIds) {
          const { data: bondData } = await supabase
            .from('social_bonds')
            .select('bond_xp')
            .eq('id', bondId)
            .single();

          if (bondData) {
            await supabase
              .from('social_bonds')
              .update({
                bond_xp: bondData.bond_xp + xpPerBond,
                last_interaction_at: new Date().toISOString(),
              })
              .eq('id', bondId);
          }
        }

        await useSocialBondsStore.getState().fetchBonds();
      }

      const newTotalXp = hobby.totalXp + xpEarned;
      const newSkillRank = getSkillRankFromXP(newTotalXp);
      const newSessionCount = hobby.sessionCount + 1;
      const newTotalTime = hobby.totalTimeMinutes + input.durationMinutes;
      const newAvgSatisfaction = (
        (hobby.averageSatisfaction * hobby.sessionCount + input.satisfactionRating) /
        newSessionCount
      );

      const currentRankThreshold = SKILL_RANK_XP_THRESHOLDS[newSkillRank];
      const nextRankIndex = Object.keys(SKILL_RANK_XP_THRESHOLDS).indexOf(newSkillRank) + 1;
      const nextRankThreshold = nextRankIndex < 6
        ? Object.values(SKILL_RANK_XP_THRESHOLDS)[nextRankIndex]
        : SKILL_RANK_XP_THRESHOLDS.Master + 10000;
      const currentXp = newTotalXp - currentRankThreshold;

      await supabase
        .from('avocational_hobbies')
        .update({
          total_xp: newTotalXp,
          current_xp: currentXp,
          skill_rank: newSkillRank,
          total_time_minutes: newTotalTime,
          session_count: newSessionCount,
          average_satisfaction: newAvgSatisfaction,
          updated_at: new Date().toISOString(),
        })
        .eq('id', hobby.id);

      const { data: domainStats } = await supabase
        .from('domain_stats')
        .select('points')
        .eq('user_id', user.id)
        .eq('domain_id', 'avocational')
        .maybeSingle();

      if (domainStats) {
        await supabase
          .from('domain_stats')
          .update({
            points: domainStats.points + xpEarned,
          })
          .eq('user_id', user.id)
          .eq('domain_id', 'avocational');
      } else {
        await supabase
          .from('domain_stats')
          .insert({
            user_id: user.id,
            domain_id: 'avocational',
            points: xpEarned,
            level: 1,
          });
      }

      await get().fetchHobbies();
      await get().fetchActivities(input.hobbyId, 20);

      return {
        id: activityData.id,
        userId: activityData.user_id,
        hobbyId: activityData.hobby_id,
        name: activityData.name,
        durationMinutes: activityData.duration_minutes,
        satisfactionRating: activityData.satisfaction_rating,
        energyType: activityData.energy_type as EnergyType,
        isSocial: activityData.is_social,
        isActive: activityData.is_active,
        isCreative: activityData.is_creative,
        notes: activityData.notes || '',
        xpEarned: activityData.xp_earned,
        modeXpDistribution,
        loggedAt: activityData.logged_at,
        createdAt: activityData.created_at,
      };
    } catch (error) {
      failed(at, error);
      return null;
    }
  },

  getHobby: (id) => {
    return get().hobbies.find(h => h.id === id);
  },

  getHobbiesByRank: (rank) => {
    return get().hobbies.filter(h => h.skillRank === rank);
  },

  getActiveHobbies: () => {
    return get().hobbies.filter(h => h.isActive);
  },

  getRecentActivities: (limit = 10) => {
    return get().activities.slice(0, limit);
  },

  getActivitiesForHobby: (hobbyId) => {
    return get().activities.filter(a => a.hobbyId === hobbyId);
  },

  getTotalAvocationalXP: () => {
    return get().hobbies.reduce((total, h) => total + h.totalXp, 0);
  },

  getTotalTimeInvested: () => {
    return get().hobbies.reduce((total, h) => total + h.totalTimeMinutes, 0);
  },

  getAverageSatisfaction: () => {
    const hobbies = get().hobbies.filter(h => h.sessionCount > 0);
    if (hobbies.length === 0) return 0;
    const totalWeightedSatisfaction = hobbies.reduce(
      (total, h) => total + h.averageSatisfaction * h.sessionCount,
      0
    );
    const totalSessions = hobbies.reduce((total, h) => total + h.sessionCount, 0);
    return totalWeightedSatisfaction / totalSessions;
  },

  getBondsForActivity: (activityId) => {
    const bondIds = get().activityBonds
      .filter(ab => ab.activityId === activityId)
      .map(ab => ab.bondId);
    const allBonds = useSocialBondsStore.getState().bonds;
    return allBonds.filter(b => bondIds.includes(b.id));
  },

  getDomainStats: () => {
    const hobbies = get().hobbies;
    const byRank: Record<SkillRank, number> = {
      Beginner: 0,
      Novice: 0,
      Intermediate: 0,
      Advanced: 0,
      Expert: 0,
      Master: 0,
    };

    let totalSessions = 0;
    let totalTimeMinutes = 0;
    let totalXP = 0;
    let weightedSatisfaction = 0;

    hobbies.forEach(h => {
      byRank[h.skillRank]++;
      totalSessions += h.sessionCount;
      totalTimeMinutes += h.totalTimeMinutes;
      totalXP += h.totalXp;
      weightedSatisfaction += h.averageSatisfaction * h.sessionCount;
    });

    return {
      totalHobbies: hobbies.length,
      activeHobbies: hobbies.filter(h => h.isActive).length,
      totalSessions,
      totalTimeHours: Math.round(totalTimeMinutes / 60 * 10) / 10,
      totalXP,
      averageSatisfaction: totalSessions > 0 ? weightedSatisfaction / totalSessions : 0,
      byRank,
    };
  },

  /** Back to empty — run on sign-out (lib/signOutReset). */
  clearStore: () => {
    epoch += 1;
    set({
      hobbies: [],
      activities: [],
      activityBonds: [],
      parameters: [],
      isLoading: false,
      error: null,
    });
  },
}));

onSignOut(() => useAvocationalStore.getState().clearStore());
